import { Backdrop, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'

import ProductFeaturesTable from './ProductFeaturesTable'
import UserDetails from './UserDetails'
import UserPermissions from './UserPermissions'
import UserProfileTitle from './UserProfileTitle'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { useMemo } from 'react'
import useOrgIcons from 'components/common/hooks/useOrgIcons'
import useRoles from 'components/common/hooks/useRoles'

const UserProfileModal = ({ open, onClose }) => {
  const { userRole, user } = useInnovaAuth()
  const { getBackColor, getTextColor } = useInnovaTheme()
  const { getCurrentOrgIcon } = useOrgIcons()
  const { features, isLoadingProductFeatures } = useRoles()

  const orgIcon = useMemo(() => getCurrentOrgIcon(), [getCurrentOrgIcon])

  const productFeatures = useMemo(() => {
    if (!Array.isArray(features)) return []

    let orgFeatures = features.find((orgs) => orgs.organization === userRole?.organization)
    if (!orgFeatures) return []

    let productDescriptions = [...new Set(orgFeatures.features?.map((item) => item.productDescription))]
    let featureDescriptions = [...new Set(orgFeatures.features?.map((item) => item.description))].sort((a, b) => {
      if (a === 'Base') return -1
      if (b === 'Base') return 1
      return a.localeCompare(b)
    })

    let productFeaturesData = []
    featureDescriptions?.forEach((feature) => {
      let row = { feature }
      productDescriptions.forEach((product) => {
        let productName = product
        if (productName === 'Mobile App') productName = 'Vision'
        if (productName === 'Web Services') productName = 'Vantage'
        if (productName === 'Third Party') productName = 'Nexus API'

        row[productName] = userRole?.roleAttributes?.features?.find(
          (roleFeat) => roleFeat.productDescription === product && roleFeat.description === feature,
        )
          ? true
          : false
      })
      productFeaturesData.push(row)
    })
    return productFeaturesData
  }, [userRole, features])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          overflow: 'hidden',
          borderRadius: 3,
          backgroundColor: getBackColor(),
          color: getTextColor(),
          border: '1px solid',
          borderColor: 'rgba(255,255,255,0.08)',
          boxShadow: '0px 24px 60px rgba(0,0,0,0.45)',
        },
      }}>
      <Backdrop
        open={isLoadingProductFeatures}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 10,
          color: '#fff',
          backdropFilter: 'blur(6px)',
          flexDirection: 'column',
          gap: 2,
        }}>
        <CircularProgress size={48} />
        <Typography>Loading user profile...</Typography>
      </Backdrop>
      <DialogTitle
        sx={{
          p: 0,
          position: 'relative',
          overflow: 'hidden',
        }}>
        <UserProfileTitle user={user} orgIcon={orgIcon} onClose={onClose} />
        <UserDetails userRole={userRole} backColor={getBackColor()} />
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pb: 2,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '70vh',
        }}>
        <UserPermissions userRole={userRole} />
        <ProductFeaturesTable
          productFeatures={productFeatures}
          getBackColor={getBackColor}
          getTextColor={getTextColor}
        />
      </DialogContent>
    </Dialog>
  )
}

export default UserProfileModal
